// Frontend plan-entitlement helpers.
// Keep these rules aligned with base/utils/plan_features.py in the backend.

export const AGIL_LEVEL = 2;
export const COMPLETO_LEVEL = 3;
export const UNLIMITED_DISPLAY_VALUE = 9999;

// subscription_packages holds each tier twice: 1-3 are the 30-day plans and
// 4-6 their 365-day twins (Essencial, Ágil, Completo in both cases). sub_lvl
// stores the raw subscription_number, so a yearly Completo subscriber carries
// 6, not 3. Compare tiers, never the raw number, or the people paying for a
// year lose the features they bought.
export const YEARLY_OFFSET = 3;

export const planTier = value => {
  const parsed = Number(value || 0);
  if (!Number.isFinite(parsed)) return 0;
  const tier = parsed > YEARLY_OFFSET ? parsed - YEARLY_OFFSET : parsed;
  return tier >= 0 && tier <= COMPLETO_LEVEL ? tier : 0;
};

// Entitlement predicates that take a raw sub_lvl and ignore billing status.
// Use these for the plain "is this plan good enough" gates; use
// hasAgilFeatures / hasCompletoFeatures when the subscription's status and
// end date matter too.
export const isCompletoTier = subLvl => planTier(subLvl) === COMPLETO_LEVEL;
export const isPaidTier = subLvl => planTier(subLvl) >= AGIL_LEVEL;

const INACTIVE_PAID_STATUSES = new Set([
  "canceled",
  "cancelled",
  "incomplete",
  "incomplete_expired",
  "unpaid",
  "paused"
]);

const toPlanLevel = planTier;

export const subscriptionHasNotEnded = restaurant => {
  if (!restaurant) return false;

  const status = String(restaurant.subscription_status || "").toLowerCase();
  if (INACTIVE_PAID_STATUSES.has(status)) return false;

  const endValue = restaurant.subscription_end_date;
  if (endValue) {
    const endDate = new Date(endValue);
    if (!Number.isNaN(endDate.getTime()) && endDate.getTime() <= Date.now()) {
      return false;
    }
  }

  return true;
};

export const hasAgilFeatures = restaurant => {
  // 2 and 5 are both Ágil, 3 and 6 both Completo.
  const level = toPlanLevel(restaurant?.sub_lvl);
  return (
    (level === AGIL_LEVEL || level === COMPLETO_LEVEL) &&
    subscriptionHasNotEnded(restaurant)
  );
};

export const hasCompletoFeatures = restaurant => {
  const level = toPlanLevel(restaurant?.sub_lvl);
  return level === COMPLETO_LEVEL && subscriptionHasNotEnded(restaurant);
};

// Essencial, or a paid plan whose subscription has lapsed.
export const isOnFreePlan = restaurant => !hasAgilFeatures(restaurant);

// Whether the diner-facing pages carry the "Reservas com Soup" mark. Free
// restaurants always carry it - it is what they pay with - so the stored
// preference only counts once the restaurant is paying. Mirrors
// plan_features.shows_soup_attribution.
export const showsSoupAttribution = restaurant => {
  if (!restaurant) return true;
  if (isOnFreePlan(restaurant)) return true;
  return !restaurant.hide_soup_attribution;
};

export const effectiveReservationPartyLimit = (restaurant, configuredValue) => {
  if (!hasAgilFeatures(restaurant)) return UNLIMITED_DISPLAY_VALUE;

  const configured = Number(
    configuredValue ?? restaurant?.limit_people_reservation ?? 0
  );
  return Number.isFinite(configured) && configured > 0
    ? configured
    : UNLIMITED_DISPLAY_VALUE;
};

export const effectiveIntervalPeopleLimit = (restaurant, configuredValue) => {
  if (!hasCompletoFeatures(restaurant)) return UNLIMITED_DISPLAY_VALUE;

  const configured = Number(
    configuredValue ?? restaurant?.accept_people_per_each_time ?? 0
  );
  return Number.isFinite(configured) && configured > 0 ? configured : 0;
};
